import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "../services/api";

const platformKeys = {
  dashboard: ["platform", "dashboard"],
  settings: ["platform", "settings"],
  staff: (query = "") => ["platform", "staff", query],
  tenants: (query = "") => ["platform", "tenants", query],
  domains: (params = {}) => ["platform", "domains", params],
  vendors: (query = "") => ["platform", "vendors", query],
  products: (query = "") => ["platform", "products", query],
  plans: (query = "") => ["platform", "plans", query],
  subscriptions: (query = "") => ["platform", "subscriptions", query],
  users: (params = {}) => ["platform", "users", params],
  userDevices: (id = "") => ["platform", "users", id, "devices"],
  logs: (params = {}) => ["platform", "logs", params],
  logDetail: (id = "") => ["platform", "logs", "detail", id],
};

const platformRoleSet = new Set(["platform_owner", "platform_admin"]);

function buildListQuery(url, query) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function buildParamsQuery(url, params = {}) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function invalidatePlatform(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["platform"] }),
    queryClient.invalidateQueries({ queryKey: ["public-config"] }),
  ]);
}

export function hasPlatformAccess(user) {
  const roles = Array.isArray(user?.platformRoles) ? user.platformRoles : [];
  return roles.some((role) => platformRoleSet.has(role));
}

export function getPlatformName(user) {
  const firstName = user?.first_name || user?.firstName || "";
  const lastName = user?.last_name || user?.lastName || "";
  return `${firstName} ${lastName}`.trim() || user?.username || user?.email || "Platform User";
}

export function usePlatformDashboard() {
  return useQuery({
    queryKey: platformKeys.dashboard,
    queryFn: async () => {
      const response = await get("/api/v1/platform/dashboard");
      // `get()` unwraps to the JSend body `{ status, data: { counts, recent* } }`.
      // Read the inner `data` payload (tolerating a flat shape) so the metric
      // cards and recent-activity lists bind to real values instead of 0/empty.
      return response?.data || response || {};
    },
    staleTime: 30 * 1000,
  });
}

export function usePlatformSettings() {
  return useQuery({
    queryKey: platformKeys.settings,
    queryFn: async () => {
      const response = await get("/api/v1/platform/settings");
      return response?.data?.item || response?.item || null;
    },
  });
}

export function useUpdatePlatformSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => patch("/api/v1/platform/settings", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

/**
 * Probe an AI provider's credentials and discover its available model ids.
 * Used by the Integrations → OpenAI / Claude dialogs to both validate an
 * API key and populate the Default-model autocomplete.
 *
 * Payload: { provider: "openai"|"anthropic", apiKey, baseUrl?, organizationId? }
 * Response shape on success: { provider, models: string[], rawCount }
 */
export function useTestAiProvider() {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await post("/api/v1/platform/settings/ai/test", payload);
      return response?.data || response || null;
    },
  });
}

export function useTestConnectMobileUssd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await post("/api/v1/platform/settings/ussd/test", payload);
      return response?.data || response || null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "logs"] });
    },
  });
}

export function useTestDunsVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await post("/api/v1/platform/settings/duns/test", payload);
      return response?.data || response || null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformKeys.settings });
      queryClient.invalidateQueries({ queryKey: ["platform", "logs"] });
    },
  });
}

/**
 * Run one live Serper (serper.dev) search to prove the platform web-search
 * credentials work. The server persists lastTestStatus / lastTestedAt /
 * lastTestMessage / lastTestQuery and writes a system log entry.
 *
 * Payload (all optional): { apiKey, apiBaseUrl, query, defaultCountry,
 * defaultLanguage }. Response data: { ok, outcome, message, checkedAt, query,
 * resultCount, topResult, creditsUsed, providerStatus }.
 *
 * Deliberately does NOT invalidate the settings query: the Settings page
 * writes the returned result into its draft, and a settings refetch would
 * re-hydrate the draft and wipe any unsaved edits (e.g. a newly typed key)
 * in the open Serper dialog.
 */
export function useTestSerperConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await post("/api/v1/platform/settings/serper/test", payload);
      return response?.data || response || null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "logs"] });
    },
  });
}

export function usePlatformStaff(query = "") {
  return useQuery({
    queryKey: platformKeys.staff(query),
    queryFn: async () => {
      const response = await get(buildListQuery("/api/v1/platform/staff", query));
      return response?.data?.items || response?.items || [];
    },
  });
}

export function usePlatformUsers(params = {}) {
  return useQuery({
    queryKey: platformKeys.users(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.q) qs.set("q", params.q);
      if (params.tenantId) qs.set("tenantId", params.tenantId);
      if (params.planId) qs.set("planId", params.planId);
      if (params.packageId) qs.set("packageId", params.packageId);
      if (params.page) qs.set("page", params.page);
      const url = `/api/v1/platform/users${qs.toString() ? `?${qs}` : ""}`;
      const response = await get(url);
      // The endpoint returns the JSend envelope `{ status, data: { items,
      // total, page, limit } }`, and `get()` already unwraps to that body.
      // Read the inner `data` payload, tolerating a flat shape too.
      return (
        response?.data ||
        response || { items: [], total: 0, page: 1, limit: 50 }
      );
    },
    keepPreviousData: true,
  });
}

export function usePlatformUserDevices(userId, options = {}) {
  return useQuery({
    queryKey: platformKeys.userDevices(userId),
    queryFn: async () => {
      const response = await get(`/api/v1/platform/users/${userId}/devices`);
      return response?.data || response || { deviceProfiles: [], recentLogs: [] };
    },
    enabled: Boolean(userId) && options.enabled !== false,
  });
}

export function usePlatformLogs(params = {}, options = {}) {
  return useQuery({
    queryKey: platformKeys.logs(params),
    queryFn: async () => {
      const response = await get(buildParamsQuery("/api/v1/platform/logs", params));
      return response || { items: [], total: 0, page: 1, limit: 50, totalPages: 1 };
    },
    keepPreviousData: true,
    ...options,
  });
}

export function usePlatformLogDetail(id, options = {}) {
  return useQuery({
    queryKey: platformKeys.logDetail(id),
    queryFn: async () => {
      const response = await get(`/api/v1/platform/logs/${id}`);
      return response || { item: null, related: [] };
    },
    enabled: Boolean(id) && options.enabled !== false,
  });
}

export function useBulkActionPlatformUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/users/batch", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

// Platform-admin override: create a brand-new Uniflo user (or attach an
// existing one by email) and optionally place them into one or more clans with
// no consent/verification flow. Returns `{ item, tempPassword, created }`.
export function useCreatePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/users", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useCreatePlatformStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/staff", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) =>
      patch(`/api/v1/platform/staff/${userId}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function usePlatformTenants(query = "") {
  return useQuery({
    queryKey: platformKeys.tenants(query),
    queryFn: async () => {
      const response = await get(buildListQuery("/api/v1/platform/tenants", query));
      // The platform tenant list endpoint returns
      // `{ status, data: { items: [...] } }` (the project's standard
      // JSend shape). Read items from `response.data.items`, but
      // tolerate the legacy/flat `response.items` shape too so this
      // keeps working if the endpoint ever switches.
      return response?.data?.items || response?.items || [];
    },
  });
}

export function useCreatePlatformTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/tenants", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      patch(`/api/v1/platform/tenants/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/tenants/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function usePlatformDomains(params = {}) {
  return useQuery({
    queryKey: platformKeys.domains(params),
    queryFn: async () => {
      const response = await get(buildParamsQuery("/api/v1/platform/domains", params));
      return response?.data || response || { items: [], platform: {} };
    },
  });
}

export function useVerifyPlatformDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => post(`/api/v1/platform/domains/${id}/verify`, {}),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }) => patch(`/api/v1/platform/domains/${id}`, { action }),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/domains/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function usePlatformVendors(query = "") {
  return useQuery({
    queryKey: platformKeys.vendors(query),
    queryFn: async () => {
      const response = await get(buildListQuery("/api/v1/platform/vendors", query));
      return response?.data?.items || response?.items || [];
    },
  });
}

export function useCreatePlatformVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/vendors", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      patch(`/api/v1/platform/vendors/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/vendors/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function usePlatformProducts(query = "") {
  return useQuery({
    queryKey: platformKeys.products(query),
    queryFn: async () => {
      const response = await get(buildListQuery("/api/v1/platform/products", query));
      return response?.data?.items || response?.items || [];
    },
  });
}

export function useCreatePlatformProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/products", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      patch(`/api/v1/platform/products/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/products/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

// Backward-compatible aliases
export const usePlatformPackages = usePlatformProducts;
export const useCreatePlatformPackage = useCreatePlatformProduct;
export const useUpdatePlatformPackage = useUpdatePlatformProduct;
export const useDeletePlatformPackage = useDeletePlatformProduct;

export function usePlatformPlans(query = "") {
  return useQuery({
    queryKey: platformKeys.plans(query),
    queryFn: async () => {
      const response = await get(buildListQuery("/api/v1/platform/plans", query));
      return response?.data?.items || response?.items || [];
    },
  });
}

export function useCreatePlatformPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/plans", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patch(`/api/v1/platform/plans/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/plans/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function usePlatformSubscriptions(query = "") {
  return useQuery({
    queryKey: platformKeys.subscriptions(query),
    queryFn: async () => {
      const response = await get(
        buildListQuery("/api/v1/platform/subscriptions", query)
      );
      return response?.data?.items || response?.items || [];
    },
  });
}

export function useCreatePlatformSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/api/v1/platform/subscriptions", payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      patch(`/api/v1/platform/subscriptions/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/subscriptions/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useUpdatePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patch(`/api/v1/platform/users/${id}`, payload),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}

export function useDeletePlatformUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/api/v1/platform/users/${id}`),
    onSuccess: () => invalidatePlatform(queryClient),
  });
}
